import '@/webgl/Features'

import { RunnerActor } from '@/state/RunnerStateMachine'
import { throwIfAborted } from '@/webgl/core/AbortSignalUtils'
import { IScene } from '@/webgl/engine/IScene'
import Renderer from '@/webgl/Renderer'
import { createScenario, ScenarioID } from '@/webgl/runner/scenarios'

import GLView from './GLView'

export default class GLApp {
  private static _instance: GLApp | null = null

  static getInstance(): GLApp {
    if (this._instance === null) {
      this._instance = new GLApp()
    }
    return this._instance
  }

  readonly glview: GLView
  readonly renderer: Renderer
  scene: IScene

  abort: AbortController

  // Set by `start()` once loading has completed. `resume()` is driven by UI pause state, which
  // can toggle while the scene is still loading; rendering then would read unloaded resources.
  private _started = false

  private constructor() {
    const canvas = document.createElement('canvas')
    this.glview = new GLView(canvas)
    this.renderer = new Renderer(this.glview)
  }

  initScenario(scenario: ScenarioID, uiActor: RunnerActor) {
    this.scene = createScenario(scenario, this.renderer)
    this.scene.setActor(uiActor)
    this.renderer.setScene(this.scene)
  }

  dispose() {
    this._started = false
    this.scene?.dispose()
    this.scene = null
    this.renderer.setScene(null)
    this.renderer.clear()
  }

  /**
   * implement loading here if the entry point has a "main" loading logic
   */
  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([this.renderer.postprocess.load(abort), this.scene.load(abort)])
    throwIfAborted(abort)
  }

  /**
   * start rendering loop on GLView
   */
  start(): void {
    this._started = true
    this.scene.start()
    this.glview.start()
  }

  /**
   * stop rendering loop on GLView
   */
  stop(): void {
    this.glview.stop()
  }

  /**
   * Resume rendering loop on GLView
   */
  resume(): void {
    if (!this._started) return
    this.glview.start()
  }
}

/// #if _DEBUG
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    location.reload()
  })
}
/// #endif
